import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { C, PageHeader, GlassCard, Button, Badge } from "@coremonit/design-system";
import { PlusCircle, ArrowLeft, ArrowRight, Check, X } from "lucide-react";
import { useWizardData } from "./wizardStore";
import { useCreateService } from "../../api/hooks";
import { Step1Details }        from "./steps/Step1Details";
import { Step2Deployment }     from "./steps/Step2Deployment";
import { Step3Instrumentation }from "./steps/Step3Instrumentation";
import { Step4Connection }     from "./steps/Step4Connection";
import { Step5Auth }           from "./steps/Step5Auth";
import { Step6Validation }     from "./steps/Step6Validation";
import { Step7Baseline }       from "./steps/Step7Baseline";
import { Step8AI }             from "./steps/Step8AI";
import { Step9Finish }         from "./steps/Step9Finish";

const STEPS = [
  { id: 1, label: "Service Details" },
  { id: 2, label: "Deployment" },
  { id: 3, label: "Instrumentation" },
  { id: 4, label: "Connection" },
  { id: 5, label: "Authentication" },
  { id: 6, label: "Validation" },
  { id: 7, label: "Baseline" },
  { id: 8, label: "AI Recommendation" },
  { id: 9, label: "Finish" },
];

export function AddServiceWizardPage() {
  const [step, setStep] = useState(1);
  const wiz = useWizardData();
  const create = useCreateService();
  const nav = useNavigate();

  const canProceed = validate(step, wiz.data);

  const next = async () => {
    if (!canProceed) return;
    if (step === 8) {
      // Create service before "Finish" screen so it shows up in fleet
      try {
        const r = await create.mutateAsync(wiz.toCreateInput());
        wiz.update({ createdServiceId: r.service.id });
        toast.success(`${r.service.name} added to your fleet`);
        setStep(9);
      } catch (e) {
        toast.error(`Create failed: ${(e as Error).message}`);
      }
      return;
    }
    setStep((s) => Math.min(9, s + 1));
  };

  const stepEl = (() => {
    switch (step) {
      case 1: return <Step1Details        data={wiz.data} update={wiz.update} />;
      case 2: return <Step2Deployment     data={wiz.data} update={wiz.update} />;
      case 3: return <Step3Instrumentation data={wiz.data} update={wiz.update} />;
      case 4: return <Step4Connection     data={wiz.data} update={wiz.update} />;
      case 5: return <Step5Auth           data={wiz.data} update={wiz.update} />;
      case 6: return <Step6Validation     data={wiz.data} update={wiz.update} />;
      case 7: return <Step7Baseline       data={wiz.data} update={wiz.update} />;
      case 8: return <Step8AI             data={wiz.data} update={wiz.update} />;
      case 9: return <Step9Finish         data={wiz.data} onDone={() => nav(`/services/${wiz.data.createdServiceId ?? ""}`)} />;
    }
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader
        icon={<PlusCircle size={16} />}
        title="Add Microservice"
        subtitle={`Step ${step} of 9 · ${STEPS[step - 1].label}`}
        actions={<Button variant="ghost" icon={<X size={12} />} onClick={() => nav("/services")}>Cancel</Button>}
      />

      <StepIndicator step={step} onJump={(s) => setStep(Math.min(s, step))} />

      <GlassCard style={{ padding: 22, minHeight: 380 }}>
        {stepEl}
      </GlassCard>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <Button
          variant="secondary"
          size="md"
          icon={<ArrowLeft size={12} />}
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
        >
          Back
        </Button>
        {step < 9 && (
          <Button
            variant={canProceed ? "glow" : "secondary"}
            size="md"
            iconRight={step === 8 ? <Check size={12} /> : <ArrowRight size={12} />}
            onClick={next}
            disabled={!canProceed || create.isPending}
            loading={create.isPending}
          >
            {step === 8 ? "Create service" : "Continue"}
          </Button>
        )}
        {step === 9 && (
          <Button variant="glow" size="md" iconRight={<ArrowRight size={12} />} onClick={() => nav(`/services/${wiz.data.createdServiceId ?? ""}`)}>
            Go to service
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────
function validate(step: number, data: ReturnType<typeof useWizardData>["data"]): boolean {
  switch (step) {
    case 1: return !!(data.name.trim() && data.owner.trim() && data.team.trim());
    case 2: return !!data.deploymentType;
    case 3: return data.instrumentation.length > 0;
    case 4: return !!data.connectionMode;
    case 5: return !!data.authMethod;
    case 6: return !!data.validationResults?.every((r) => r.status === "passed");
    case 7: return !!data.baseline;
    case 8: return !!data.aiRecommendation;
    default: return true;
  }
}

function StepIndicator({ step, onJump }: { step: number; onJump: (s: number) => void }) {
  return (
    <GlassCard style={{ padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {STEPS.map((s, i) => {
          const done = step > s.id;
          const active = step === s.id;
          return (
            <div key={s.id} style={{ display: "flex", alignItems: "center", flex: i === STEPS.length - 1 ? 0 : 1, minWidth: 0 }}>
              <button
                onClick={() => onJump(s.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 10px", borderRadius: 20,
                  background: active ? C.blueDim : "transparent",
                  border: `1px solid ${active ? C.borderBright : done ? "rgba(16,185,129,0.4)" : C.border}`,
                  color: active ? C.blue : done ? C.green : C.textMuted,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontSize: 10, fontWeight: 700,
                }}
              >
                <span style={{
                  width: 18, height: 18, borderRadius: "50%",
                  background: active ? C.blue : done ? C.green : C.surfaceMid,
                  color: active || done ? "#fff" : C.textMuted,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, fontWeight: 700,
                }}>
                  {done ? <Check size={10} /> : s.id}
                </span>
                <span style={{ display: window.innerWidth < 900 && !active ? "none" : "inline" }}>{s.label}</span>
              </button>
              {i < STEPS.length - 1 && (
                <div style={{ flex: 1, minWidth: 12, height: 1, background: done ? C.green : C.border, margin: "0 6px" }} />
              )}
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
}
